import React, { useEffect, useLayoutEffect, useMemo, useState, useCallback } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { Close, ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const DESIGN = {
  navy: "#0a0f2e",
  blue: "#1565C0",
  blueBright: "#2979FF",
  accent: "#00E5FF",
  accentSoft: "rgba(0,229,255,0.12)",
  surface: "#ffffff",
  border: "rgba(21,101,192,0.13)",
  text: "#0d1b3e",
  textMid: "#3a4a6b",
  textSoft: "#7a8fb5",
  radius: "16px",
  radiusSm: "10px",
};

const STORAGE_KEY = "documentos_tour_seen_v2";
const DEFAULT_CARD_WIDTH = 500;
const CARD_MARGIN = 16; // separacion minima respecto de los bordes de la ventana

export const getDocumentosTourSteps = (t) => [
  {
    id: "intro",
    eyebrow: null,
    title: t("tour.documentos.intro.title"),
    description: t("tour.documentos.intro.description"),
    target: null,
    width: 520,
    primaryLabel: t("tour.documentos.intro.primaryLabel"),
  },
  {
    id: "paso1",
    eyebrow: null,
    title: t("tour.documentos.paso1.title"),
    description: t("tour.documentos.paso1.description"),
    target: '[data-tour="upload-button"]',
    width: 460,
    primaryLabel: t("tour.documentos.paso1.primaryLabel"),
  },
  {
    id: "paso2",
    eyebrow: null,
    title: t("tour.documentos.paso2.title"),
    description: t("tour.documentos.paso2.description"),
    target: '[data-tour="folder-grid"]',
    anchorToTarget: true,
    primaryLabel: t("tour.documentos.paso2.primaryLabel"),
  },
  {
    id: "paso3",
    eyebrow: null,
    title: t("tour.documentos.paso3.title"),
    description: t("tour.documentos.paso3.description"),
    target: '[data-tour="folder-grid"]',
    anchorToTarget: false,
    primaryLabel: t("tour.documentos.paso3.primaryLabel"),
  },
  {
    id: "paso4",
    eyebrow: null,
    title: t("tour.documentos.paso4.title"),
    description: t("tour.documentos.paso4.description"),
    target: '[data-tour="upload-button"]',
    primaryLabel: t("tour.documentos.paso4.primaryLabel"),
  },
  {
    id: "final",
    eyebrow: t("tour.documentos.final.eyebrow"),
    title: t("tour.documentos.final.title"),
    description: t("tour.documentos.final.description"),
    target: null,
    primaryLabel: t("tour.documentos.final.primaryLabel"),
  },
];

export const useDocumentosTour = ({ enabled = true } = {}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch {
      // localStorage no disponible (SSR, modo privado, etc.) -> no autoabrir
    }
  }, [enabled]);

  const dismiss = useCallback(() => setOpen(false), []);

  const complete = useCallback(() => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
  }, []);

  const reopen = useCallback(() => setOpen(true), []);

  return { open, dismiss, complete, reopen };
};

const isFullyVisible = (rect) =>
  rect.top >= 0 &&
  rect.left >= 0 &&
  rect.bottom <= window.innerHeight &&
  rect.right <= window.innerWidth;

const useTargetRect = (selector, active) => {
  const [state, setState] = useState({ rect: null, measured: false });

  useLayoutEffect(() => {
    if (!active || !selector) {
      setState({ rect: null, measured: true });
      return;
    }

    const update = () => {
      const el = document.querySelector(selector);
      if (!el) {
        setState({ rect: null, measured: true });
        return;
      }

      const r = el.getBoundingClientRect();
      if (!isFullyVisible(r)) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      setState({
        rect: { top: r.top, left: r.left, width: r.width, height: r.height },
        measured: true
      });
    };

    update(); // Calculate immediately to prevent layout flashes

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [selector, active]);

  return state;
};

const PADDING = 10;

const SpotlightCutout = ({ rect }) => {
  if (!rect) return null;
  const top = rect.top - PADDING;
  const left = rect.left - PADDING;
  const width = rect.width + PADDING * 2;
  const height = rect.height + PADDING * 2;

  return (
    <Box
      sx={{
        position: "fixed",
        top, left, width, height,
        borderRadius: "14px",
        boxShadow: "0 0 0 9999px rgba(5,8,26,0.72)",
        border: `2px solid ${DESIGN.accent}`,
        pointerEvents: "none",
        zIndex: 1301,
        transition: "top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease",
        animation: "docTourPulse 1.8s ease-in-out infinite",
        "@keyframes docTourPulse": {
          "0%, 100%": { boxShadow: "0 0 0 9999px rgba(5,8,26,0.72), 0 0 0 0 rgba(0,229,255,0.45)" },
          "50%": { boxShadow: "0 0 0 9999px rgba(5,8,26,0.72), 0 0 0 6px rgba(0,229,255,0.15)" },
        },
      }}
    />
  );
};

// --------------------------------------------------------------------------------
// Calcula dónde va la tarjeta:
//  - Si el paso tiene target (rect disponible): pegada ABAJO-IZQUIERDA del
//    recuadro resaltado (spotlight), con clamp para que nunca se salga de
//    la ventana (ni por abajo, ni por los costados).
//  - Si no hay target (pasos "intro"/"final"): centrada en pantalla, como
//    antes.
// --------------------------------------------------------------------------------
// Se eliminó useCardPosition hook porque causaba renderizados en cascada (layout flashes)

const DocumentosOnboardingTour = ({ open, onDismiss, onComplete, steps: providedSteps }) => {
  const { t } = useTranslation();
  const steps = providedSteps || getDocumentosTourSteps(t);
  const [index, setIndex] = useState(0);

  useEffect(() => { if (open) setIndex(0); }, [open]);

  const step = steps[index];
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;

  const { rect, measured } = useTargetRect(step?.target, open);
  const cardWidth = step?.width || DEFAULT_CARD_WIDTH;
  const estimatedHeight = 280;

  const shouldAnchor = Boolean(step?.anchorToTarget && rect);

  const anchoredPosition = useMemo(() => {
    if (!shouldAnchor || !rect) return null;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = rect.left - PADDING;
    let top = rect.top + rect.height + PADDING + 12;

    left = Math.min(left, vw - cardWidth - CARD_MARGIN);
    left = Math.max(left, CARD_MARGIN);

    if (top + estimatedHeight > vh - CARD_MARGIN) {
      const topAbove = rect.top - PADDING - 12 - estimatedHeight;
      top = topAbove >= CARD_MARGIN ? topAbove : Math.max(CARD_MARGIN, vh - estimatedHeight - CARD_MARGIN);
    }
    return { top, left };
  }, [shouldAnchor, rect, cardWidth, estimatedHeight]);

  const handleNext = useCallback(() => {
    if (isLast) { onComplete?.(); return; }
    setIndex((i) => Math.min(steps.length - 1, i + 1));
  }, [isLast, onComplete, steps.length]);

  const handleBack = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  if (!open || typeof document === "undefined") return null;

  let positionSx;
  if (step?.anchorToTarget && !measured) {
    // Si debe anclarse pero aún no sabemos si el elemento existe en el DOM,
    // lo ocultamos para que no parpadee al centro en el primer render.
    positionSx = { position: "fixed", visibility: "hidden" };
  } else if (shouldAnchor && anchoredPosition) {
    positionSx = {
      position: "fixed",
      top: anchoredPosition.top,
      left: anchoredPosition.left,
      transform: "none",
    };
  } else {
    // Si no hay que anclar (o no se encontró el elemento), va al centro.
    positionSx = {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  }

  return createPortal(
    <AnimatePresence mode="wait">
      <Box key="tour-root">
        {rect ? (
          <SpotlightCutout rect={rect} />
        ) : (
          <Box
            sx={{
              position: "fixed", inset: 0, zIndex: 1300,
              bgcolor: "rgba(5,8,26,0.72)",
            }}
          />
        )}

        {/* IMPORTANTE: igual que antes, el centrado/posicionamiento va en
            este Box PLANO (sin Framer Motion), con "position/top/left" (y
            "transform: translate(-50%,-50%)" solo cuando corresponde
            centrar). El motion.div de abajo va ANIDADO ADENTRO, sin tocar
            "position" ni "top/left" — solo maneja la animación de entrada
            /salida (opacity, y, scale), para no pisar el transform de acá. */}
        <Box
          sx={{
            ...positionSx,
            zIndex: 1302, width: cardWidth, maxWidth: "calc(100vw - 32px)",
          }}
        >
          <motion.div
            key={step.id}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.22 }}
          >
            <Box
              sx={{
                borderRadius: DESIGN.radius,
                bgcolor: DESIGN.surface,
                boxShadow: "0 24px 64px rgba(5,8,26,0.45)",
                // border: `2px solid ${DESIGN.navy}`,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 3.5, pt: 3, pb: 2.5,
                  background: `linear-gradient(135deg, ${DESIGN.navy} 0%, #0d1b3e 100%)`,
                  position: "relative",
                }}
              >
                <IconButton
                  onClick={onDismiss}
                  size="small"
                  aria-label="Cerrar tutorial"
                  sx={{
                    position: "absolute", top: 12, right: 12,
                    width: 32, height: 32, color: "rgba(255,255,255,0.6)",
                    "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>

                {step.eyebrow && (
                  <Box
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.5,
                      px: 1.1, py: 0.35, mb: 1, borderRadius: "999px",
                      bgcolor: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.3)",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: DESIGN.accent, letterSpacing: 1 }}>
                      {step.eyebrow}
                    </Typography>
                  </Box>
                )}

                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.25rem", letterSpacing: -0.3, pr: 4 }}>
                  {step.title}
                </Typography>
              </Box>

              <Box sx={{ px: 3.5, py: 3 }}>
                <Typography
                  sx={{
                    fontSize: "1rem", color: DESIGN.textMid, lineHeight: 1.7,
                    fontWeight: 600,
                    whiteSpace: "pre-line", mb: 3,
                  }}
                >
                  {step.description}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {steps.map((s, i) => (
                      <Box
                        key={s.id}
                        sx={{
                          width: i === index ? 16 : 6, height: 6, borderRadius: 3,
                          bgcolor: i === index ? DESIGN.blue : DESIGN.border,
                          transition: "all 0.25s ease",
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    {!isFirst && (
                      <Button
                        onClick={handleBack}
                        size="small"
                        startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
                        sx={{
                          textTransform: "none", fontWeight: 700, fontSize: "0.9rem",
                          color: DESIGN.textSoft, "&:hover": { color: DESIGN.blue, bgcolor: DESIGN.accentSoft },
                          px: 2, py: 1,
                        }}
                      >
                        Atrás
                      </Button>
                    )}
                    <Button
                      onClick={handleNext}
                      variant="contained"
                      size="medium"
                      endIcon={!isLast ? <ArrowForwardIos sx={{ fontSize: 13 }} /> : null}
                      sx={{
                        textTransform: "none", fontWeight: 700, fontSize: "0.95rem",
                        borderRadius: DESIGN.radiusSm, px: 3, py: 1,
                        background: `linear-gradient(135deg, ${DESIGN.blue}, ${DESIGN.blueBright})`,
                        boxShadow: "none",
                        "&:hover": { background: "linear-gradient(135deg, #1251a3, #1f5fcc)" },
                      }}
                    >
                      {step.primaryLabel}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </motion.div>
        </Box>
      </Box>
    </AnimatePresence>,
    document.body
  );
};

export default DocumentosOnboardingTour;